import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { notify } from '@/components/core';

const UIActionsContext = createContext(null);

export const UIActionsProvider = ({ children }) => {
  const navigate = useNavigate();

  const onNavigate = useCallback(
    (to, opts = {}) => {
      if (!to) return;
      if (/^https?:\/\//.test(to)) {
        window.location.href = to;
        return;
      }
      try {
        navigate(to);
      } catch (e) {
        console.warn("UIActions: navigate failed", e, to);
      }
    },
    [navigate]
  );

  const setActivePage = useCallback((pageName) => {
    console.debug("[UIActions] setActivePage:", pageName);
  }, []);

  const onSelectPartner = useCallback((partner) => {
    if (partner?.id) onNavigate(`/partners/${partner.id}`);
  }, [onNavigate]);

  const onSelectTenant = useCallback((tenant) => {
    if (tenant?.id) onNavigate(`/tenants/${tenant.id}`);
  }, [onNavigate]);

  const onSelectAsset = useCallback((asset) => {
    if (asset?.id) onNavigate(`/business-apps/${asset.id}`);
  }, [onNavigate]);

  // Shim: expose addNotification via notify so any component using
  // useUIActions().addNotification continues to work during the transition.
  const addNotification = useCallback((message, type = 'success') => {
    if (type === 'error') notify.error(message);
    else if (type === 'warning') notify.warning(message);
    else if (type === 'info') notify.info(message);
    else notify.success(message);
  }, []);

  const value = useMemo(
    () => ({
      onNavigate,
      setActivePage,
      onSelectPartner,
      onSelectTenant,
      onSelectAsset,
      addNotification,
      notify, // Export notify directly
    }),
    [onNavigate, setActivePage, onSelectPartner, onSelectTenant, onSelectAsset, addNotification]
  );

  return <UIActionsContext.Provider value={value}>{children}</UIActionsContext.Provider>;
};

export const useUIActions = () => {
  const ctx = useContext(UIActionsContext);
  if (!ctx) {
    return {
      onNavigate: (t) => (window.location.href = t),
      setActivePage: () => { },
      onSelectPartner: () => { },
      onSelectTenant: () => { },
      onSelectAsset: () => { },
      addNotification: () => { },
      notify: {
        success: console.log,
        error: console.error,
        info: console.log,
        warning: console.warn,
      }
    };
  }
  return ctx;
};
